// pages/grant-form.ts — the 3-step grant application form.
//
// THIS IS THE HIGHEST-RISK PAGE IN THE APP (readme-frontend-2 §8).
// All API behaviour is preserved byte-identical from frontend2:
//
//   • validateStep1 — 13 required fields + $500–$10,000 range.
//   • buildBody    — UI field names → API column names, including
//                    tri-state "I don't know" → 'Unknown' mapping
//                    that matches the DB CHECK constraint.
//   • Hydrate path — reverse mapping when resuming a draft.
//   • Document upload — multipart for real bytes, JSON for URL links.
//   • Submit confirm — two-click via Bootstrap modal; the modal's
//                      "Yes, submit" button is the only path that
//                      actually fires api.submitGrant.
//
// Only the visual layer (Bootstrap classes / on-brand CSS) has been
// swapped. The form's state lives in a closure inside mount(); we
// re-render the body region whenever step changes, otherwise we let
// inputs stay uncontrolled and read values back at persist time.

import type { Page, PageContext } from '../router';
import { html, esc, fmtMoney } from '../util';

// ─── Types ────────────────────────────────────────────────────────
interface Form {
  projectName: string;
  taxStatus:   'Yes' | 'No' | "I don't know";
  taxNotes:    string;
  orgName:     string;
  ein:         string;
  description: string;
  firstName:   string;
  lastName:    string;
  email:       string;
  phone:       string;
  street:      string;
  city:        string;
  state:       string;
  zip:         string;
  amount:      string;
  startDate:   string;
  endDate:     string;
  narrative:   string;
  confirmAccurate: boolean;
  confirmLocked:   boolean;
}

interface Doc {
  DocumentId: number;
  DocumentType: string;
  FileName: string;
  FileSize: number;
  FileExtension?: string;
  FilePath?: string;
  UploadedAt?: string;
}

interface GrantRow {
  GrantId: number;
  ProjectName: string;
  TaxExemptStatus: string;
  TaxStatusNotes?: string;
  OrganizationLegalName: string;
  TaxId: string;
  ProjectDescription: string;
  ContactFirstName: string;
  ContactLastName: string;
  ContactEmail: string;
  ContactPhone?: string;
  AddressStreet?: string;
  AddressCity?: string;
  AddressState?: string;
  AddressZip?: string;
  RequestedAmount: number | null;
  ProjectStartDate?: string;
  ProjectEndDate?: string;
  ImpactNarrative?: string;
}

// ─── Initial form state ───────────────────────────────────────────
function makeInitialForm(): Form {
  return {
    projectName: '', taxStatus: 'Yes', taxNotes: '',
    orgName: '', ein: '', description: '',
    firstName: '', lastName: '', email: '', phone: '',
    street: '', city: '', state: '', zip: '',
    amount: '', startDate: '', endDate: '', narrative: '',
    confirmAccurate: false, confirmLocked: false,
  };
}

// ─── Field-name mapping (UI → API). Byte-identical to frontend2. ──
function buildBody(f: Form): Record<string, unknown> {
  return {
    ProjectName:           f.projectName,
    TaxExemptStatus:       f.taxStatus === "I don't know" ? 'Unknown' : f.taxStatus,
    TaxStatusNotes:        f.taxNotes   || null,
    OrganizationLegalName: f.orgName,
    TaxId:                 f.ein,
    ProjectDescription:    f.description,
    ContactFirstName:      f.firstName,
    ContactLastName:       f.lastName,
    ContactEmail:          f.email,
    ContactPhone:          f.phone     || null,
    AddressStreet:         f.street    || null,
    AddressCity:           f.city      || null,
    AddressState:          f.state     || null,
    AddressZip:            f.zip       || null,
    RequestedAmount:       f.amount ? Number(f.amount) : null,
    ProjectStartDate:      f.startDate || null,
    ProjectEndDate:        f.endDate   || null,
    ImpactNarrative:       f.narrative || null,
  };
}

function hydrateForm(g: GrantRow): Form {
  return {
    projectName:  g.ProjectName              ?? '',
    taxStatus:    g.TaxExemptStatus === 'Unknown' ? "I don't know" : ((g.TaxExemptStatus as Form['taxStatus']) ?? 'Yes'),
    taxNotes:     g.TaxStatusNotes           ?? '',
    orgName:      g.OrganizationLegalName    ?? '',
    ein:          g.TaxId                    ?? '',
    description:  g.ProjectDescription       ?? '',
    firstName:    g.ContactFirstName         ?? '',
    lastName:     g.ContactLastName          ?? '',
    email:        g.ContactEmail             ?? '',
    phone:        g.ContactPhone             ?? '',
    street:       g.AddressStreet            ?? '',
    city:         g.AddressCity              ?? '',
    state:        g.AddressState             ?? '',
    zip:          g.AddressZip               ?? '',
    amount:       g.RequestedAmount != null ? String(g.RequestedAmount) : '',
    startDate:    g.ProjectStartDate ? g.ProjectStartDate.slice(0, 10) : '',
    endDate:      g.ProjectEndDate   ? g.ProjectEndDate.slice(0, 10)   : '',
    narrative:    g.ImpactNarrative          ?? '',
    confirmAccurate: false, confirmLocked: false,
  };
}

// ─── validateStep1 — byte-identical to frontend2 ──────────────────
function validateStep1(f: Form): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!f.projectName?.trim()) errs.projectName = 'Required.';
  if (!f.orgName?.trim())     errs.orgName     = 'Required.';
  if (!f.ein?.trim())         errs.ein         = 'Required.';
  if (!f.description?.trim()) errs.description = 'Required.';
  if (!f.firstName?.trim())   errs.firstName   = 'Required.';
  if (!f.lastName?.trim())    errs.lastName    = 'Required.';
  if (!f.email?.trim())       errs.email       = 'Required.';
  if (!f.street?.trim())      errs.street      = 'Required.';
  if (!f.city?.trim())        errs.city        = 'Required.';
  if (!f.state?.trim())       errs.state       = 'Required.';
  if (!f.zip?.trim())         errs.zip         = 'Required.';
  if (!f.narrative?.trim())   errs.narrative   = 'Required.';
  const amt = Number(f.amount);
  if (!String(f.amount ?? '').trim()) errs.amount = 'Required.';
  else if (isNaN(amt) || amt < 500 || amt > 10000) errs.amount = 'Must be between $500 and $10,000.';
  return errs;
}

// ─── Stepper render ───────────────────────────────────────────────
function renderStepper(step: number): string {
  const steps = [
    { num: 1, label: 'Identify' },
    { num: 2, label: 'Upload' },
    { num: 3, label: 'Submit' },
  ];
  const items = steps.map(s => {
    const done   = step > s.num;
    const active = step === s.num;
    const bg     = done || active ? 'var(--primary)' : 'var(--paper-3)';
    const fg     = done || active ? 'var(--paper)'   : 'var(--ink-soft)';
    const border = done || active ? 'var(--primary)' : 'var(--rule-2)';
    const labelColor = active ? 'var(--primary)'
                     : done   ? 'var(--st-approved)'
                     :          'var(--ink-soft)';
    return html`
      <div class="gf-step-cell">
        <div class="gf-step-bubble" style="background:${bg};color:${fg};border-color:${border};">
          ${done ? '\u2713' : String(s.num)}
        </div>
        <span class="t-small" style="font-weight: 500; color: ${labelColor};">${s.label}</span>
      </div>
    `;
  }).join('');
  return html`
    <div class="gf-stepper">
      <div class="gf-stepper-line"></div>
      ${{ __raw: items }}
    </div>
  `;
}

// ─── Field rendering helper ───────────────────────────────────────
function field(opts: {
  name:        keyof Form;
  label:       string;
  type?:       'text' | 'email' | 'tel' | 'number' | 'date' | 'textarea';
  placeholder?: string;
  required?:   boolean;
  hint?:       string;
  error?:      string;
  value:       string;
  min?:        number;
  max?:        number;
  rows?:       number;
}): string {
  const id = `gf-${opts.name}`;
  const isTextarea = opts.type === 'textarea';
  const errCls = opts.error ? ' is-invalid' : '';
  const star   = opts.required ? '<span style="color: var(--st-denied); margin-left: 2px;">*</span>' : '';
  const inputAttr =
    `class="form-control${errCls}" id="${id}" name="${opts.name}"` +
    (opts.placeholder ? ` placeholder="${esc(opts.placeholder)}"` : '') +
    (opts.required ? ' required' : '');

  const control = isTextarea
    ? `<textarea ${inputAttr} rows="${opts.rows ?? 4}" style="resize: vertical;">${esc(opts.value)}</textarea>`
    : `<input ${inputAttr} type="${opts.type || 'text'}"` +
      (opts.min !== undefined ? ` min="${opts.min}"` : '') +
      (opts.max !== undefined ? ` max="${opts.max}"` : '') +
      ` value="${esc(opts.value)}"/>`;

  return html`
    <div class="gf-field">
      <label class="form-label" for="${id}">${opts.label}${{ __raw: star }}</label>
      ${{ __raw: control }}
      ${{ __raw: opts.error
        ? html`<div class="gf-field-error">${opts.error}</div>`
        : opts.hint
          ? html`<div class="form-text">${opts.hint}</div>`
          : '' }}
    </div>
  `;
}

// ─── Step 1 render ────────────────────────────────────────────────
function renderStep1(form: Form, errors: Record<string, string>): string {
  const taxOpts = ['Yes', 'No', "I don't know"] as const;
  const taxRadios = taxOpts.map(opt => {
    const active = form.taxStatus === opt;
    const bg = active ? 'var(--primary-soft)' : 'var(--card)';
    const bd = active ? 'var(--primary)'      : 'var(--rule-2)';
    return `
      <label class="gf-tax-radio" style="background: ${bg}; border-color: ${bd};">
        <input type="radio" name="taxStatus" value="${esc(opt)}"${active ? ' checked' : ''}
               style="accent-color: var(--primary);"/>
        ${esc(opt)}
      </label>
    `;
  }).join('');

  const taxNotesField = form.taxStatus !== 'Yes' ? html`
    <div class="gf-field" style="margin-bottom: 0;">
      <label class="form-label" for="gf-taxNotes">Please describe your organization's tax-exempt status</label>
      <textarea class="form-control" id="gf-taxNotes" name="taxNotes" rows="3"
                placeholder="e.g., We're a fiscally sponsored project, applying for 501(c)(3) status…"
                style="resize: vertical;">${esc(form.taxNotes)}</textarea>
      <div class="form-text">Optional — this helps our reviewers understand your situation</div>
    </div>
  ` : '';

  return html`
    <div class="card" style="padding: 24px;">
      <h2 class="t-h3 serif" style="margin: 0; font-size: 22px;">Step 1 · Identify your project</h2>
      <p class="t-body" style="margin: 4px 0 24px; font-size: 14px;">
        Tell us about your organization and the project you'd like to fund.
      </p>

      ${{ __raw: field({ name: 'projectName', label: 'Project name', required: true,
          placeholder: 'e.g., Community Garden Initiative',
          hint: 'Max 500 characters — be specific and descriptive',
          value: form.projectName, error: errors.projectName }) }}

      <!-- 501(c)(3) tri-state -->
      <div class="gf-tax-panel">
        <label class="form-label">Is your organization a registered 501(c)(3)?
          <span style="color: var(--st-denied); margin-left: 2px;">*</span>
        </label>
        <div class="d-flex" style="gap: 12px; margin-bottom: 14px; flex-wrap: wrap;">
          ${{ __raw: taxRadios }}
        </div>
        ${{ __raw: taxNotesField }}
        <div class="gf-info">
          We can only fund registered 501(c)(3) organizations.
          <a href="https://www.irs.gov/charities-non-profits/charitable-organizations/exemption-requirements-501c3-organizations"
             target="_blank" rel="noopener noreferrer" style="color: var(--primary);">IRS info</a> ·
          <a href="https://en.wikipedia.org/wiki/501(c)(3)_organization"
             target="_blank" rel="noopener noreferrer" style="color: var(--primary);">Wikipedia</a>
        </div>
      </div>

      <div class="gf-row-2col">
        ${{ __raw: field({ name: 'orgName', label: 'Organization legal name', required: true,
            placeholder: 'e.g., River Valley Community Center',
            value: form.orgName, error: errors.orgName }) }}
        ${{ __raw: field({ name: 'ein', label: 'Tax ID / EIN', required: true,
            placeholder: '12-3456789', hint: 'Format: XX-XXXXXXX',
            value: form.ein, error: errors.ein }) }}
      </div>

      ${{ __raw: field({ name: 'description', label: 'Brief description', required: true,
          type: 'textarea', rows: 3,
          placeholder: 'Summarize your project and its impact on the community…',
          hint: 'Max 1,000 characters',
          value: form.description, error: errors.description }) }}

      <div class="gf-section-divider">
        <h3 class="t-h4 serif" style="margin: 0 0 16px; font-size: 18px;">Contact information</h3>
        <div class="gf-row-2col">
          ${{ __raw: field({ name: 'firstName', label: 'First name', required: true,
              placeholder: 'First name', value: form.firstName, error: errors.firstName }) }}
          ${{ __raw: field({ name: 'lastName', label: 'Last name', required: true,
              placeholder: 'Last name', value: form.lastName, error: errors.lastName }) }}
          ${{ __raw: field({ name: 'email', label: 'Email', required: true, type: 'email',
              placeholder: 'contact@example.org', value: form.email, error: errors.email }) }}
          ${{ __raw: field({ name: 'phone', label: 'Phone', type: 'tel',
              placeholder: '(555) 000-0000', value: form.phone }) }}
        </div>
        ${{ __raw: field({ name: 'street', label: 'Street address', required: true,
            placeholder: '123 Main St', value: form.street, error: errors.street }) }}
        <div class="gf-row-3col">
          ${{ __raw: field({ name: 'city', label: 'City', required: true,
              placeholder: 'Greenville', value: form.city, error: errors.city }) }}
          ${{ __raw: field({ name: 'state', label: 'State', required: true,
              placeholder: 'ST', value: form.state, error: errors.state }) }}
          ${{ __raw: field({ name: 'zip', label: 'ZIP', required: true,
              placeholder: '12345', value: form.zip, error: errors.zip }) }}
        </div>
      </div>

      <div class="gf-section-divider">
        <h3 class="t-h4 serif" style="margin: 0 0 16px; font-size: 18px;">Grant details</h3>
        <div class="gf-row-2col">
          ${{ __raw: field({ name: 'amount', label: 'Requested amount', required: true,
              type: 'number', min: 500, max: 10000, placeholder: '2500',
              hint: '$500 – $10,000', value: form.amount, error: errors.amount }) }}
          <div></div>
          ${{ __raw: field({ name: 'startDate', label: 'Project start date',
              type: 'date', value: form.startDate }) }}
          ${{ __raw: field({ name: 'endDate', label: 'Project end date',
              type: 'date', value: form.endDate }) }}
        </div>
        ${{ __raw: field({ name: 'narrative', label: 'Impact narrative', required: true,
            type: 'textarea', rows: 5,
            placeholder: 'Tell us how this project will make a difference…',
            hint: 'Max 5,000 characters — describe your goals, methods, and expected outcomes',
            value: form.narrative, error: errors.narrative }) }}
      </div>

      <div class="gf-actions">
        <button type="button" class="btn btn-outline-primary" data-action="save-draft">Save draft</button>
        <button type="button" class="btn btn-primary" data-action="continue-step1">Continue to upload →</button>
      </div>
    </div>
  `;
}

// ─── Step 2 render ────────────────────────────────────────────────
const DOC_SLOTS = [
  { key: 'proposal',  label: '1. Grant proposal',
    desc: 'Project scope and impact statement',
    req: true,  formats: 'PDF, DOCX, XLSX, PPTX, MD, TXT', type: 'Proposal' },
  { key: 'irsLetter', label: '2. IRS Determination Letter',
    desc: 'Proof of 501(c)(3) tax-exempt status',
    req: true,  formats: 'PDF only', type: 'IRS Letter' },
  { key: 'budget',    label: '3. Budget documentation',
    desc: 'Detailed budget breakdown for your project',
    req: false, formats: 'PDF, XLSX, DOCX', type: 'Budget' },
] as const;

function fmtFileSize(b: number | undefined): string {
  if (b == null) return '\u2014';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function renderStep2(documents: Doc[]): string {
  const slots = DOC_SLOTS.map(slot => {
    const uploaded = documents.find(d => d.DocumentType === slot.type);
    const badge = slot.req
      ? '<span class="gf-badge gf-badge--required">Required</span>'
      : '<span class="gf-badge">Optional</span>';

    const body = uploaded
      ? `
        <div class="gf-doc-uploaded">
          <div class="d-flex" style="gap: 12px; align-items: center;">
            <span style="color: var(--primary); font-size: 18px;">✓</span>
            <div>
              <div style="font-weight: 500; font-size: 14px;">${esc(uploaded.FileName)}</div>
              <div class="t-small">${esc(fmtFileSize(uploaded.FileSize))} · Uploaded</div>
            </div>
          </div>
          <button type="button" class="gf-doc-remove"
                  data-action="remove-doc" data-doc-id="${uploaded.DocumentId}">Remove</button>
        </div>
      `
      : `
        <div class="gf-dropzone" data-doc-key="${slot.key}" data-doc-type="${esc(slot.type)}">
          <p style="margin: 0; font-weight: 500; font-size: 14px;">Click to upload or drag and drop</p>
          <p class="t-small" style="margin: 6px 0 0;">${esc(slot.formats)} · Max 10 MB</p>
          <input type="file" hidden data-doc-input="${slot.key}"/>
        </div>
      `;

    return `
      <div class="gf-doc-slot">
        <div class="d-flex" style="justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <div>
            <h4 class="serif" style="margin: 0; font-size: 16px;">${esc(slot.label)}</h4>
            <p class="t-small" style="margin: 0;">${esc(slot.desc)}</p>
          </div>
          ${badge}
        </div>
        ${body}
      </div>
    `;
  }).join('');

  const linkDocs = documents.filter(d => /^https?:\/\//i.test(d.FilePath || ''));
  const linkList = linkDocs.map(d => `
    <div class="d-flex" style="justify-content: space-between; align-items: center; margin-top: 10px; font-size: 13.5px;">
      <a href="${esc(d.FilePath || '')}" target="_blank" rel="noopener noreferrer"
         style="color: var(--primary);">${esc(d.FileName)}</a>
      <button type="button" class="gf-doc-remove" data-action="remove-doc" data-doc-id="${d.DocumentId}">Remove</button>
    </div>
  `).join('');

  return html`
    <div class="card" style="padding: 24px;">
      <h2 class="t-h3 serif" style="margin: 0; font-size: 22px;">Step 2 · Upload documents</h2>
      <p class="t-body" style="margin: 4px 0 24px; font-size: 14px;">
        We accept PDF, DOCX, XLSX, PPTX, MD, and TXT formats (max 10 MB per file).
      </p>

      <div class="gf-info gf-info--tips">
        <strong>Tips for a strong application</strong>
        <ul style="margin: 8px 0 0; padding-left: 18px; font-size: 13.5px; line-height: 1.7;">
          <li>Name files clearly: <em>ProjectName_Proposal.pdf</em></li>
          <li>Ensure PDFs are searchable, not scanned images</li>
          <li>Your IRS Determination Letter must be current and valid</li>
          <li>Include letters of support if available</li>
        </ul>
      </div>

      ${{ __raw: slots }}

      <!-- URL-link alternative -->
      <div class="card gf-link-card">
        <h4 class="serif" style="margin: 0; font-size: 16px;">Have a video or large file?</h4>
        <p class="t-small" style="margin: 6px 0 12px;">
          Upload to Google Drive, Dropbox, or YouTube and paste the link below.
        </p>
        <div class="d-flex" style="gap: 8px;">
          <input type="url" class="form-control" id="gf-link-url" placeholder="https://drive.google.com/…" style="flex: 1;"/>
          <button type="button" class="btn btn-outline-primary" data-action="add-link" disabled>Add link</button>
        </div>
        ${{ __raw: linkList }}
      </div>

      <div class="gf-actions" style="margin-top: 24px;">
        <button type="button" class="btn btn-outline-primary" data-action="back-step1">← Back</button>
        <div class="d-flex" style="gap: 12px;">
          <button type="button" class="btn btn-outline-primary" data-action="save-draft">Save draft</button>
          <button type="button" class="btn btn-primary" data-action="continue-step2">Continue to review →</button>
        </div>
      </div>
    </div>
  `;
}

// ─── Step 3 render ────────────────────────────────────────────────
function renderStep3(form: Form, documents: Doc[]): string {
  const projInfo: Array<[string, string]> = [
    ['Project name',     form.projectName],
    ['Organization',     form.orgName],
    ['EIN',              form.ein],
    ['501(c)(3) status', form.taxStatus === 'Yes' ? 'Yes — verified' : form.taxStatus],
    ['Requested amount', form.amount ? fmtMoney(form.amount) : '\u2014'],
    ['Contact',
     [form.firstName, form.lastName].filter(Boolean).join(' ') + (form.email ? ` · ${form.email}` : '')],
  ];
  const projRows = projInfo.map(([k, v]) => `
    <div class="d-flex" style="gap: 16px; margin-bottom: 10px; font-size: 14px;">
      <span style="color: var(--ink-soft); min-width: 160px; flex-shrink: 0;">${esc(k)}</span>
      <span style="font-weight: 500;">${esc(v)}</span>
    </div>
  `).join('');

  const docRows = documents.length === 0
    ? '<p class="t-body" style="margin: 0;">No documents uploaded yet.</p>'
    : documents.map(doc => `
      <div class="d-flex" style="gap: 12px; align-items: center; margin-bottom: 10px;">
        <span style="color: var(--primary);">✓</span>
        <span class="t-small" style="min-width: 160px;">${esc(doc.DocumentType)}</span>
        <span style="font-size: 14px; font-weight: 500;">${esc(doc.FileName)}</span>
      </div>
    `).join('');

  return html`
    <div class="card" style="padding: 24px;">
      <h2 class="t-h3 serif" style="margin: 0; font-size: 22px;">Step 3 · Review &amp; submit</h2>
      <p class="t-body" style="margin: 4px 0 24px; font-size: 14px;">
        Please review everything carefully. Your application is locked after submission.
      </p>

      <div class="gf-review-section">
        <div class="d-flex" style="justify-content: space-between; align-items: center; margin-bottom: 14px;">
          <h4 class="serif" style="margin: 0; font-size: 17px;">Project information</h4>
          <button type="button" class="btn btn-link btn-sm" data-action="back-step1">Edit →</button>
        </div>
        ${{ __raw: projRows }}
      </div>

      <div class="gf-review-section">
        <div class="d-flex" style="justify-content: space-between; align-items: center; margin-bottom: 14px;">
          <h4 class="serif" style="margin: 0; font-size: 17px;">Uploaded documents</h4>
          <button type="button" class="btn btn-link btn-sm" data-action="back-step2">Edit →</button>
        </div>
        ${{ __raw: docRows }}
      </div>

      <div class="gf-confirm-block">
        <label class="gf-confirm">
          <input type="checkbox" name="confirmAccurate"${form.confirmAccurate ? ' checked' : ''}/>
          <span style="font-size: 14px;">
            I confirm that all information provided is accurate and complete to the best of my knowledge.
          </span>
        </label>
        <label class="gf-confirm" style="margin-top: 14px;">
          <input type="checkbox" name="confirmLocked"${form.confirmLocked ? ' checked' : ''}/>
          <span style="font-size: 14px;">
            I understand that my application cannot be edited after submission, and that only
            registered 501(c)(3) organizations are eligible for funding.
          </span>
        </label>
      </div>

      <div class="gf-info gf-info--warn">
        <strong>Before you submit:</strong> Ensure your IRS Determination Letter is current and valid.
        Applications from non-501(c)(3) organizations will be denied.
      </div>

      <div class="gf-actions" style="margin-top: 24px;">
        <button type="button" class="btn btn-outline-primary" data-action="back-step2">← Back</button>
        <button type="button" class="btn btn-primary btn-lg"
                data-action="open-submit-modal"
                ${form.confirmAccurate && form.confirmLocked ? '' : 'disabled'}>
          Submit grant application
        </button>
      </div>
    </div>
  `;
}

// ─── Submit-confirm modal (Bootstrap) ─────────────────────────────
function renderSubmitModal(): string {
  return html`
    <div class="modal fade" id="gfSubmitModal" tabindex="-1" aria-labelledby="gfSubmitModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" id="gfSubmitModalLabel" style="margin: 0; font-size: 22px;">
              Submit your application?
            </h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="t-body" style="margin-bottom: 16px;">
              You are about to submit your grant application. Once submitted, it cannot be edited
              unless a reviewer requests changes.
            </p>
            <div class="gf-info gf-info--warn">
              This action cannot be undone. Make sure all information is accurate and your
              IRS Determination Letter is uploaded.
            </div>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary btn-lg" data-action="confirm-submit">
              Yes, submit application
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ─── Page object ──────────────────────────────────────────────────
const GrantForm: Page = {
  guard(ctx: PageContext): string | null {
    return ctx.user ? null : 'login';
  },

  render(_ctx: PageContext): string {
    return html`
      <nav class="wrap" style="padding: 20px 40px 0; max-width: 1240px;">
        <div class="t-small" style="color: var(--ink-soft);">
          <a class="gf-breadcrumb" data-nav="user-dashboard">Dashboard</a>
          <span style="margin: 0 8px; opacity: 0.5;">/</span>
          <span style="color: var(--ink-2);">New grant application</span>
        </div>
      </nav>

      <main class="wrap" style="max-width: 820px; padding: 32px 24px 96px;">
        <div style="margin-bottom: 32px;">
          <div class="eyebrow">Grant application</div>
          <h1 class="t-h2" style="margin: 16px 0 0;">Begin your grant journey.</h1>
          <p class="t-body" style="margin: 8px 0 0;">Cultivating change, one grant at a time.</p>
        </div>

        <div id="gf-stepper"></div>
        <div id="gf-body"></div>
        ${{ __raw: renderSubmitModal() }}
      </main>
    `;
  },

  mount(root: HTMLElement, ctx: PageContext): void {
    let alive = true;

    // ── State ────────────────────────────────────────────────────
    let step      = 1;
    const _gfIdRaw = ctx.params.get('id');
    const _gfIdNum = _gfIdRaw == null ? NaN : Number(_gfIdRaw);
    let grantId: number | null = Number.isFinite(_gfIdNum) ? _gfIdNum : null;
    let saving    = false;
    let documents: Doc[] = [];
    let errors: Record<string, string> = {};
    let form: Form = makeInitialForm();
    const modalEl   = root.querySelector('#gfSubmitModal')   as HTMLElement | null;
    const stepperEl = root.querySelector('#gf-stepper')      as HTMLElement | null;
    const bodyEl    = root.querySelector('#gf-body')         as HTMLElement | null;

    function repaint(): void {
      if (stepperEl) stepperEl.innerHTML = renderStepper(step);
      if (bodyEl) {
        if (step === 1) bodyEl.innerHTML = renderStep1(form, errors);
        else if (step === 2) bodyEl.innerHTML = renderStep2(documents);
        else bodyEl.innerHTML = renderStep3(form, documents);
      }
      // The current step's form inputs are now in DOM; nothing else needed.
    }

    // ── Read DOM → form (uncontrolled) ──────────────────────────
    function readFormFromDom(): void {
      if (!bodyEl) return;
      const inputs = bodyEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[name], textarea[name]');
      inputs.forEach(el => {
        const name = el.getAttribute('name');
        if (!name) return;
        if ((el as HTMLInputElement).type === 'radio') {
          if ((el as HTMLInputElement).checked) (form as Record<string, unknown>)[name] = el.value;
        } else if ((el as HTMLInputElement).type === 'checkbox') {
          (form as Record<string, unknown>)[name] = (el as HTMLInputElement).checked;
        } else {
          (form as Record<string, unknown>)[name] = el.value;
        }
      });
    }

    // ── Hydrate from an existing grant if id given ──────────────
    async function hydrate(): Promise<void> {
      if (grantId == null) return;
      try {
        const [g, docs] = await Promise.all([
          window.api.getGrant(grantId),
          window.api.listDocuments(grantId),
        ]);
        if (!alive) return;
        form      = hydrateForm(g as GrantRow);
        documents = (docs as Doc[]) || [];
        repaint();
      } catch (e) { console.error(e); }
    }

    // ── Persist (POST or PATCH) ─────────────────────────────────
    async function persist(): Promise<unknown> {
      const body = buildBody(form);
      if (grantId) return await window.api.updateGrant(grantId, body);
      const g = await window.api.createGrant(body) as { GrantId: number };
      grantId = g.GrantId;
      return g;
    }

    // ── Step transitions ────────────────────────────────────────
    async function saveDraft(): Promise<void> {
      readFormFromDom();
      if (!form.projectName?.trim()) {
        errors = { projectName: 'A project name is required to save a draft.' };
        repaint();
        return;
      }
      errors = {};
      saving = true;
      try { await persist(); ctx.navigate('user-dashboard'); }
      catch (err) { alert((err as Error).message); }
      finally { saving = false; }
    }

    async function continueStep1(): Promise<void> {
      readFormFromDom();
      const errs = validateStep1(form);
      if (Object.keys(errs).length > 0) { errors = errs; repaint(); return; }
      errors = {};
      saving = true;
      try { await persist(); step = 2; repaint(); }
      catch (err) { alert((err as Error).message); }
      finally { saving = false; }
    }

    async function continueStep2(): Promise<void> {
      readFormFromDom();
      saving = true;
      try { await persist(); step = 3; repaint(); }
      catch (err) { alert((err as Error).message); }
      finally { saving = false; }
    }

    async function refreshDocs(): Promise<void> {
      if (grantId == null) return;
      try {
        const docs = await window.api.listDocuments(grantId) as Doc[];
        if (alive) { documents = docs || []; repaint(); }
      } catch (e) { console.error(e); }
    }

    async function handleUpload(slotKey: string, file: File): Promise<void> {
      if (grantId == null) {
        alert('Please complete Step 1 first.');
        step = 1; repaint();
        return;
      }
      const slot = DOC_SLOTS.find(s => s.key === slotKey);
      if (!slot) return;
      saving = true;
      try {
        await window.api.uploadDocument(grantId, file, slot.type);
        await refreshDocs();
      } catch (err) {
        const e = err as { payload?: { error?: string; allowed?: string[] }; message?: string };
        const code = e.payload?.error;
        if (code === 'extension_not_allowed') {
          alert(`File type not allowed. Accepted: ${(e.payload?.allowed || []).join(', ')}.`);
        } else if (code === 'file_too_large') {
          alert('File is too large. Max 10 MB.');
        } else {
          alert(e.message || 'Upload failed.');
        }
      } finally { saving = false; }
    }

    async function handleAddLink(): Promise<void> {
      const input = bodyEl?.querySelector<HTMLInputElement>('#gf-link-url');
      if (!input) return;
      const raw = input.value.trim();
      if (grantId == null) {
        alert('Please complete Step 1 first.');
        step = 1; repaint();
        return;
      }
      if (!/^https?:\/\//i.test(raw)) {
        alert('Enter a full URL beginning with http:// or https://');
        return;
      }
      saving = true;
      try {
        let displayName = raw;
        try {
          const u = new URL(raw);
          const tail = u.pathname.split('/').filter(Boolean).pop() || u.hostname;
          displayName = `${u.hostname}/${tail}`;
        } catch { /* keep raw url */ }
        await window.api.addDocument(grantId, {
          DocumentType: 'Supporting',
          FileName: displayName, FileSize: 0, FileExtension: 'url', Url: raw,
        });
        await refreshDocs();
      } catch (err) { alert((err as Error).message); }
      finally { saving = false; }
    }

    async function handleRemoveDoc(docId: number): Promise<void> {
      if (grantId == null) return;
      saving = true;
      try { await window.api.deleteDocument(grantId, docId); await refreshDocs(); }
      catch (err) { alert((err as Error).message); }
      finally { saving = false; }
    }

    async function handleConfirmSubmit(): Promise<void> {
      if (grantId == null) return;
      // Hide the modal.
      if (modalEl) {
        const Bs = (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(el: HTMLElement): { hide(): void } } } }).bootstrap;
        Bs?.Modal.getOrCreateInstance(modalEl).hide();
      }
      saving = true;
      try {
        const grant = await window.api.submitGrant(grantId);
        if (!alive) return;
        ctx.navigate('grant-success', { id: grantId }, { grant });
      } catch (err) {
        const e = err as { payload?: { error?: string }; message?: string };
        if (e.payload?.error === 'irs_letter_required') {
          alert('Please upload your IRS Determination Letter on Step 2 before submitting.');
        } else {
          alert(e.message || 'Submit failed.');
        }
      } finally { saving = false; }
    }

    function openSubmitModal(): void {
      readFormFromDom();
      if (!modalEl) return;
      const Bs = (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(el: HTMLElement): { show(): void } } } }).bootstrap;
      Bs?.Modal.getOrCreateInstance(modalEl).show();
    }

    // ── Event delegation ────────────────────────────────────────
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      // Dropzone click → trigger hidden file input
      const dz = t.closest('.gf-dropzone[data-doc-key]') as HTMLElement | null;
      if (dz) {
        const key = dz.getAttribute('data-doc-key');
        const input = bodyEl?.querySelector<HTMLInputElement>(`[data-doc-input="${key}"]`);
        input?.click();
        return;
      }

      const actionEl = t.closest('[data-action]') as HTMLElement | null;
      if (!actionEl) return;
      const action = actionEl.getAttribute('data-action');
      switch (action) {
        case 'save-draft':         void saveDraft(); break;
        case 'continue-step1':     void continueStep1(); break;
        case 'continue-step2':     void continueStep2(); break;
        case 'back-step1':         readFormFromDom(); step = 1; repaint(); break;
        case 'back-step2':         readFormFromDom(); step = 2; repaint(); break;
        case 'open-submit-modal':  openSubmitModal(); break;
        case 'confirm-submit':     void handleConfirmSubmit(); break;
        case 'add-link':           void handleAddLink(); break;
        case 'remove-doc': {
          const id = Number(actionEl.getAttribute('data-doc-id'));
          if (id) void handleRemoveDoc(id);
          break;
        }
      }
    });

    // File-input change (delegated)
    root.addEventListener('change', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const fileInput = t.closest('[data-doc-input]') as HTMLInputElement | null;
      if (fileInput) {
        const key = fileInput.getAttribute('data-doc-input') || '';
        const file = fileInput.files?.[0];
        if (file) void handleUpload(key, file);
        fileInput.value = '';
        return;
      }
      const taxRadio = t.closest('input[name="taxStatus"]') as HTMLInputElement | null;
      if (taxRadio) {
        // The "I don't know" / "No" choices reveal the tax-notes textarea;
        // re-render step 1 to apply.
        readFormFromDom();
        repaint();
        return;
      }
      const confirmChk = t.closest('input[name="confirmAccurate"], input[name="confirmLocked"]') as HTMLInputElement | null;
      if (confirmChk) {
        readFormFromDom();
        // Toggle the submit-button disabled state without a full repaint.
        const submitBtn = bodyEl?.querySelector<HTMLButtonElement>('[data-action="open-submit-modal"]');
        if (submitBtn) submitBtn.disabled = !(form.confirmAccurate && form.confirmLocked);
      }
    });

    // input → clear that field's error if any, and enable Add Link button.
    root.addEventListener('input', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const linkUrl = t.closest('#gf-link-url') as HTMLInputElement | null;
      if (linkUrl) {
        const addBtn = bodyEl?.querySelector<HTMLButtonElement>('[data-action="add-link"]');
        if (addBtn) addBtn.disabled = !linkUrl.value.trim();
        return;
      }
      const fieldEl = t.closest('input[name], textarea[name]') as HTMLElement | null;
      if (fieldEl) {
        const name = fieldEl.getAttribute('name') || '';
        if (errors[name]) {
          delete errors[name];
          fieldEl.classList.remove('is-invalid');
          const err = fieldEl.parentElement?.querySelector('.gf-field-error');
          if (err) err.remove();
        }
      }
    });

    // Drag + drop on the dropzones.
    root.addEventListener('dragover', (e) => {
      const dz = (e.target as HTMLElement | null)?.closest('.gf-dropzone[data-doc-key]') as HTMLElement | null;
      if (dz) { e.preventDefault(); dz.classList.add('is-dragover'); }
    });
    root.addEventListener('dragleave', (e) => {
      const dz = (e.target as HTMLElement | null)?.closest('.gf-dropzone[data-doc-key]') as HTMLElement | null;
      if (dz) dz.classList.remove('is-dragover');
    });
    root.addEventListener('drop', (e) => {
      const dz = (e.target as HTMLElement | null)?.closest('.gf-dropzone[data-doc-key]') as HTMLElement | null;
      if (!dz) return;
      e.preventDefault();
      dz.classList.remove('is-dragover');
      const file = (e as DragEvent).dataTransfer?.files?.[0];
      const key = dz.getAttribute('data-doc-key') || '';
      if (file && key) void handleUpload(key, file);
    });

    // First paint + hydrate.
    repaint();
    void hydrate();

    (GrantForm as Page & { _alive?: () => void })._alive = () => { alive = false; };
  },

  unmount(): void {
    const stop = (GrantForm as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

export default GrantForm;
